import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Divider,
  Stack,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckCircleOutline, ErrorOutline, OpenInNew } from '@mui/icons-material';
import Navbar from './Navbar';
import PlayGenId from './PlagGenId';
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from '../../config/firebase';
import { collection, query, getDocs, where } from 'firebase/firestore';

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(145deg, #1a1e24, #15191e)',
  borderRadius: '16px',
  boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.25)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  height: 'calc(100vh - 160px)',
  display: 'flex',
  flexDirection: 'column',
}));

const CardScrollContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2),
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#0f172a',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#334155',
    borderRadius: '4px',
    '&:hover': {
      background: '#3b82f6',
    },
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #6366f1 0%, #3b82f6 100%)',
  color: '#fff',
  fontWeight: 600,
  padding: '12px 28px',
  borderRadius: '12px',
  textTransform: 'none',
  letterSpacing: '0.5px',
  '&:hover': {
    background: 'linear-gradient(45deg, #4f46e5 0%, #2563eb 100%)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  },
  '&:disabled': {
    background: '#2d3748',
    color: '#718096'
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: '12px',
    background: '#0f172a',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'border-color 0.3s ease',
    '&:hover': {
      borderColor: '#3b82f6'
    },
    '&.Mui-focused': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)'
    }
  },
  '& .MuiInputBase-input': {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    color: '#f8fafc'
  }
}));

const PlagiarismChecker = () => {
  const location = useLocation();
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (location.state?.essayText) {
      setText(location.state.essayText);
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await fetchUsername(user.email);
      } else {
        setCurrentUser(null);
        setUsername('');
      }
    });

    return () => unsubscribe();
  }, [location.state]);

  const fetchUsername = async (email) => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        setUsername(userDoc.id);
      }
    } catch (error) {
      console.error('Error fetching username:', error);
    }
  };

  const checkPlagiarism = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post('http://localhost:5000/check', { text });
      setResult(res.data);
    } catch (err) {
      console.error('Error checking plagiarism', err);
    } finally {
      setLoading(false);
    }
  };

  const truncateUrl = (url) => url.replace(/^(https?:\/\/)?(www\.)?/, '').slice(0, 40);

  const SimilarityProgress = ({ value }) => (
    <Tooltip title={`${value}% Similarity Score`} arrow>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: '#1e293b',
          '& .MuiLinearProgress-bar': {
            borderRadius: 5,
            background: value >= 50 ? 'linear-gradient(45deg, #ef4444, #dc2626)' 
                        : 'linear-gradient(45deg, #22c55e, #16a34a)'
          }
        }}
      />
    </Tooltip>
  );

  return (
    <div>
      <Navbar />
      <Box sx={{ 
        p: { xs: 2, md: 4 }, 
        minHeight: '100vh', 
        background: '#000000',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Grid container spacing={3} sx={{ flex: 1 }}>
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%',
                paddingBottom: '16px !important'
              }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                    Input Text
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                      Paste or type your content to analyze
                    </Typography>
                  </Typography>
                </Box>
                
                <CardScrollContainer>
                  <StyledTextField
                    multiline
                    fullWidth
                    placeholder="Enter your text here..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    sx={{
                      minHeight: '300px',
                      '& textarea': {
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#334155',
                          borderRadius: '3px',
                        },
                      }
                    }}
                  />
                </CardScrollContainer>

                <GradientButton
                  onClick={checkPlagiarism}
                  disabled={loading || !text.trim()}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    'Analyze Originality'
                  )}
                </GradientButton>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%',
                paddingBottom: '16px !important'
              }}>
                <Box sx={{ mb: 2, position: 'sticky', top: 0, bgcolor: '#1a1e24', zIndex: 1 }}>
                  <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                    Plagiarism Report
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                      Detailed originality assessment
                    </Typography>
                  </Typography>
                </Box>

                <CardScrollContainer>
                  {result ? (
                    <>
                      <Stack spacing={2} sx={{ background: '#0f172a', p: 3, borderRadius: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                            Overall Originality Score
                          </Typography>
                          <Chip
                            label={`${100 - result.average_similarity}%`}
                            color={result.average_similarity >= 50 ? 'error' : 'success'}
                            variant="outlined"
                            sx={{ fontWeight: 700, fontSize: '1.1rem' }}
                          />
                        </Stack>
                        <SimilarityProgress value={result.average_similarity} />
                      </Stack>

                      {result.matches.length > 0 ? (
                        <Stack spacing={3} sx={{ mt: 3 }}>
                          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                          
                          <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500 }}>
                            Detected Similar Content
                          </Typography>

                          {result.matches.map((match, idx) => (
                            <Stack key={idx} spacing={1.5} sx={{ p: 2, background: '#0f172a', borderRadius: 2 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 500 }}>
                                  {truncateUrl(match.source)}
                                  <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
                                </Typography>
                                <Chip
                                  label={`${match.score}% match`}
                                  size="small"
                                  sx={{ 
                                    background: 'rgba(239, 68, 68, 0.15)', 
                                    color: '#ef4444',
                                    fontWeight: 500
                                  }}
                                />
                              </Stack>
                              
                              <Typography variant="body2" sx={{ 
                                color: '#94a3b8', 
                                fontStyle: 'italic',
                                whiteSpace: 'pre-wrap'
                              }}>
                                "{match.chunk}"
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Stack alignItems="center" spacing={2} sx={{ p: 3, textAlign: 'center' }}>
                          <CheckCircleOutline sx={{ fontSize: 60, color: '#22c55e' }} />
                          <Typography variant="body1" sx={{ color: '#22c55e', fontWeight: 600 }}>
                            No significant matches found. Content appears original.
                          </Typography>
                        </Stack>
                      )}
                      <PlayGenId
                        username={username}
                        recentText={text}
                        accuracyScore={result.average_similarity}
                      />
                    </>
                  ) : (
                    <Stack spacing={2} sx={{ 
                      background: '#0f172a', 
                      p: 3, 
                      borderRadius: 2,
                      textAlign: 'center'
                    }}>
                      <ErrorOutline sx={{ fontSize: 60, color: '#3b82f6', mx: 'auto' }} />
                      <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                        Analysis results will appear here after processing.
                      </Typography>
                    </Stack>
                  )}
                </CardScrollContainer>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default PlagiarismChecker;